import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import mealApi from "../../../../api/meal/mealApi";
import {
  isRequestCancelled,
  toRejectPayload,
} from "../../../../helpers/ApiError";
import { handleRequestError } from "../../../../helpers/HandleThunkError";
import {
  ensureDownloadFileExtension,
  parseFileNameFromContentDisposition,
  triggerBrowserDownload,
} from "../../../../helpers/Helpers";

export const useMealFormDownload = () => {
  const navigate = useNavigate();
  const controllerRef = useRef<AbortController | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      const controller = controllerRef.current;
      controllerRef.current = null;
      controller?.abort();
    };
  }, []);

  const clearDownloadError = useCallback(() => {
    setDownloadError(null);
  }, []);

  const downloadForm = useCallback(
    async (uuid: string, fallbackFileName?: string): Promise<boolean> => {
      if (!uuid || controllerRef.current) {
        return false;
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      setIsDownloading(true);
      setDownloadError(null);

      try {
        const { blob, contentDisposition } = await mealApi.downloadMealForm(
          uuid,
          controller.signal,
        );

        if (controller.signal.aborted) {
          return false;
        }

        const parsedFileName = parseFileNameFromContentDisposition(
          contentDisposition,
          fallbackFileName,
        );

        const fileName = ensureDownloadFileExtension(parsedFileName, blob);

        triggerBrowserDownload(blob, fileName);
        return true;
      } catch (err) {
        if (isRequestCancelled(err, controller.signal)) {
          return false;
        }

        const rejectPayload = toRejectPayload(
          err,
          "Gagal mengunduh data meal. Silakan coba lagi.",
        );

        setDownloadError(rejectPayload.message);
        handleRequestError(rejectPayload, {
          navigate,
          showToast: true,
        });
        return false;
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
          setIsDownloading(false);
        }
      }
    },
    [navigate],
  );

  return {
    downloadForm,
    isDownloading,
    downloadError,
    clearDownloadError,
  };
};

export default useMealFormDownload;
